"use client";

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";
import {
  LiquidSurface,
  groupSquishStyle,
  shouldGroupSquish,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
} from "./liquid-surface";

export interface LiquidPillGroupVerticalItem {
  value: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
  trailing?: React.ReactNode;
}

export interface LiquidPillGroupVerticalProps {
  items: LiquidPillGroupVerticalItem[];
  activeValue?: string | null;
  onChange?: (value: string) => void;
  onHover?: (value: string | null) => void;
  size?: "xxs" | "xs" | "sm" | "md";
  disableBounce?: boolean;
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  className?: never;
}

type BubbleRect = { top: number; left: number; width: number; height: number };

const SLIDE_MS = 560;
const SLIDE_EASE = "cubic-bezier(0.22,1,0.36,1)";

export function LiquidPillGroupVertical({
  items,
  activeValue,
  onChange,
  onHover,
  size = "sm",
  disableBounce = false,
  bounceIntensity = "strong",
  deformAxis = "vertical",
}: LiquidPillGroupVerticalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRef    = useRef<HTMLDivElement>(null);
  const itemRefs     = useRef<(HTMLButtonElement | null)[]>([]);

  const [renderBubble, setRenderBubble] = useState<BubbleRect | null>(null);
  const [animate, setAnimate]           = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  const prevBubbleRect    = useRef<BubbleRect | null>(null);
  const prevDisplayTarget = useRef<string | null>(null);

  const displayTarget = hoveredValue ?? activeValue ?? null;
  const displayItem = items.find((i) => i.value === displayTarget);
  const displayVariant = resolveCircleActionVariant(displayItem?.variant ?? "slate");

  const triggerBounce = useCallback(() => {
    if (!shouldGroupSquish(bounceIntensity)) return;
    const el = bubbleRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetHeight;
    el.style.animation = "lpgv-vertical-bounce 560ms cubic-bezier(0.34,1.2,0.64,1) both";
  }, [bounceIntensity]);

  const measureAt = useCallback((target: string) => {
    const idx = items.findIndex((i) => i.value === target);
    const el  = itemRefs.current[idx];
    const container = containerRef.current;
    if (!el || !container) return null;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    return {
      top:    eRect.top  - cRect.top,
      left:   eRect.left - cRect.left,
      width:  eRect.width,
      height: eRect.height,
    };
  }, [items]);

  useLayoutEffect(() => {
    if (!displayTarget) {
      setRenderBubble(null);
      setAnimate(false);
      prevDisplayTarget.current = null;
      prevBubbleRect.current = null;
      return;
    }

    const pos = measureAt(displayTarget);
    if (!pos) return;

    const wasNull = prevDisplayTarget.current == null;
    const prevTarget = prevDisplayTarget.current;
    const moved = prevTarget != null && prevTarget !== displayTarget;
    const prevRect = prevBubbleRect.current;

    prevDisplayTarget.current = displayTarget;

    if (wasNull) {
      prevBubbleRect.current = pos;
      setRenderBubble(pos);
      setAnimate(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      return;
    }

    prevBubbleRect.current = pos;
    setRenderBubble(pos);
    if (moved && animate && !disableBounce && prevRect) triggerBounce();
  }, [displayTarget, measureAt, animate, disableBounce, triggerBounce]);

  useEffect(() => {
    if (!displayTarget) return;
    const ro = new ResizeObserver(() => {
      const pos = measureAt(displayTarget);
      if (pos) {
        prevBubbleRect.current = pos;
        setRenderBubble(pos);
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [displayTarget, measureAt]);

  const gapClass  =
    size === "xxs" ? "gap-px" : size === "xs" ? "gap-0.5" : "gap-1";
  const padClass  =
    size === "xxs"
      ? "px-1.5 py-[3px]"
      : size === "xs"
        ? "px-2 py-1"
        : size === "sm"
          ? "px-2.5 py-1.5"
          : "px-2.5 py-1.5";
  const textClass =
    size === "xxs"
      ? "text-[9px]"
      : size === "xs"
        ? "text-[10px]"
        : size === "sm"
          ? "text-[11px]"
          : "text-[12px]";
  const itemGapClass = size === "xxs" ? "gap-1" : "gap-2";

  const slideTransition = disableBounce
    ? [
        `top ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `left ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `width ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `height ${SLIDE_MS}ms ${SLIDE_EASE}`,
        "opacity 200ms ease",
      ].join(",")
    : [
        `top ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `left ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `width ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `height ${SLIDE_MS}ms ${SLIDE_EASE}`,
        "opacity 240ms ease",
      ].join(",");

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex flex-col items-start w-full",
        gapClass,
        "overflow-visible",
      )}
      onMouseLeave={() => {
        setHoveredValue(null);
        onHover?.(null);
      }}
    >
      <div
        ref={bubbleRef as React.Ref<HTMLDivElement>}
        aria-hidden
        className="lpgv-bubble pointer-events-none absolute"
        style={{
          top: renderBubble?.top ?? 0,
          left: renderBubble?.left ?? 0,
          width: renderBubble?.width ?? 0,
          height: renderBubble?.height ?? 0,
          opacity: renderBubble ? 1 : 0,
          ...groupSquishStyle(bounceIntensity, deformAxis),
          transition: animate ? slideTransition : "opacity 240ms ease",
        }}
      >
        <LiquidSurface
          as="div"
          variant={displayVariant}
          tone="tinted"
          shape="pill"
          size="free"
          shadow="neutral"
          interactive={false}
          cursorGlow={false}
          reactive
          hoverDeform={false}
          hoverBounce={false}
          bounceIntensity={bounceIntensity}
          style={{ position: "absolute", inset: 0 }}
        />
      </div>

      {items.map((item, idx) => {
        const isActive = item.value === activeValue;
        const variant  = resolveCircleActionVariant(item.variant ?? "slate");
        const vars     = circleActionVariantCssVars(variant);

        return (
          <button
            key={item.value}
            ref={(el) => { itemRefs.current[idx] = el; }}
            type="button"
            disabled={item.disabled}
            onClick={() => onChange?.(item.value)}
            onMouseEnter={() => {
              setHoveredValue(item.value);
              onHover?.(item.value);
            }}
            style={vars as React.CSSProperties}
            className={cn(
              "lpgv-item relative z-[1] inline-flex w-full max-w-full items-center justify-between gap-2 rounded-full",
              "select-none font-medium tracking-[-0.01em] text-left",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1",
              "disabled:pointer-events-none disabled:opacity-40",
              "motion-safe:[transition:color_280ms_ease,transform_300ms_cubic-bezier(0.34,1.56,0.64,1)]",
              padClass,
              textClass,
              isActive ? "lpgv-item--active" : "lpgv-item--idle",
            )}
          >
            <span className={cn("inline-flex min-w-0 flex-1 items-center", itemGapClass)}>
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {item.label != null && (
                <span className="truncate whitespace-nowrap">{item.label}</span>
              )}
            </span>
            {item.trailing != null && (
              <span className="flex-shrink-0">{item.trailing}</span>
            )}
          </button>
        );
      })}

      <style jsx global>{`
        @keyframes lpgv-vertical-bounce {
          0% {
            transform: scaleX(1) scaleY(1);
          }
          24% {
            transform:
              scaleX(calc(1 - 0.04 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 + 0.12 * var(--lp-squish-intensity, 1.55)));
          }
          52% {
            transform:
              scaleX(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 - 0.05 * var(--lp-squish-intensity, 1.55)));
          }
          76% {
            transform:
              scaleX(calc(1 - 0.012 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)));
          }
          100% {
            transform: scaleX(1) scaleY(1);
          }
        }

        .lpgv-item--idle        { color: rgb(71 85 105); }
        .dark .lpgv-item--idle  { color: rgb(212 212 216); }
        .lpgv-item--active      { color: var(--gb-accent-color); }
        .dark .lpgv-item--active { color: var(--gb-accent-color-dark); }
        .lpgv-item:not(:disabled):active { transform: scale(0.985); }
        .lpgv-item--idle:not(:disabled):hover       { color: var(--gb-accent-color); }
        .dark .lpgv-item--idle:not(:disabled):hover { color: var(--gb-accent-color-dark); }

        .lpgv-item--idle img, .lpgv-item--idle svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.85;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lpgv-item--idle img, .lpgv-item--idle svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.7;
        }
        .lpgv-item--idle:not(:disabled):hover img,
        .lpgv-item--idle:not(:disabled):hover svg {
          opacity: 0.95;
        }
        .dark .lpgv-item--idle:not(:disabled):hover img,
        .dark .lpgv-item--idle:not(:disabled):hover svg {
          opacity: 0.92;
        }
        .lpgv-item--active img, .lpgv-item--active svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.95;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lpgv-item--active img, .lpgv-item--active svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.92;
        }
      `}</style>
    </div>
  );
}
